import logo_fournitest from '../assets/LogoFournitest.svg'
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../hooks/cart';
import { useWishlist } from '../hooks/wishlist';
import { PiShoppingCartSimple } from "react-icons/pi";
import { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { MdDashboard } from "react-icons/md";
import { FiMenu, FiX, FiSearch } from "react-icons/fi";
import { TiArrowSortedDown } from "react-icons/ti";
import { MdKeyboardArrowRight } from "react-icons/md";
import { AiOutlineHeart } from "react-icons/ai";
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const { cart } = useCart();
    const { wishlistCount } = useWishlist();
    const { user } = useAuth();
    const location = useLocation();
    
    const { fetchCategories, fetchSubCategories } = useApi();
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState({});
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState([]);
    const [showMegaMenu, setShowMegaMenu] = useState(false);
    const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
    const navigate = useNavigate();
    let megaMenuTimeout = null;
    
    // Check if current page should have border
    const shouldShowBorder = location.pathname !== '/' && location.pathname !== '/aboutus';
    
    useEffect(() => {
        const loadCategoriesAndSubCategories = async () => {
            try {
                const [categoriesData, subCategoriesData] = await Promise.all([
                    fetchCategories(),
                    fetchSubCategories()
                ]);
                
                const validCategories = Array.isArray(categoriesData) ? categoriesData : [];
                const validSubCategories = Array.isArray(subCategoriesData) ? subCategoriesData : [];
                
                setCategories(validCategories);
                
                const groupedSubCategories = {};
                validSubCategories.forEach(sub => {
                    if (!groupedSubCategories[sub.category_id]) {
                        groupedSubCategories[sub.category_id] = [];
                    }
                    groupedSubCategories[sub.category_id].push(sub);
                });
                setSubCategories(groupedSubCategories);
            } catch (err) {
                console.error('Error fetching data:', err);
                setCategories([]);
                setSubCategories({});
            }
        };
        loadCategoriesAndSubCategories();
    }, [i18n.language, fetchCategories, fetchSubCategories]);

    const handleCategoryClick = (categoryTitle, categoryId) => {
        navigate(`/products/${categoryTitle}`, { 
            state: { categoryId } 
        });
        setShowMegaMenu(false);
        setMobileMenuOpen(false);
    };

    const handleSubCategoryClick = (categoryTitle, subCategoryTitle, categoryId, subCategoryId) => {
        navigate(`/products/${categoryTitle}/${subCategoryTitle}`, { 
            state: { 
                categoryId: categoryId, 
                subCategoryId: subCategoryId 
            } 
        });
        setShowMegaMenu(false);
        setMobileMenuOpen(false);
        setExpandedCategories([]);
    };

    const handleMouseEnter = () => {
        if (megaMenuTimeout) {
            clearTimeout(megaMenuTimeout);
        }
        setShowMegaMenu(true);
        if (Array.isArray(categories) && categories.length > 0 && !hoveredCategoryId) {
            setHoveredCategoryId(categories[0].id);
        }
    };

    const handleMouseLeave = () => {
        megaMenuTimeout = setTimeout(() => {
            setShowMegaMenu(false);
            setHoveredCategoryId(null);
        }, 200);
    };

    const toggleCategory = (categoryId) => {
        if (expandedCategories.includes(categoryId)) {
            setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
        } else {
            setExpandedCategories([...expandedCategories, categoryId]);
        }
    };

    return (
        <nav className={`absolute top-0 left-0 right-0 w-full z-50 ${shouldShowBorder ? 'bg-white border-b border-gray-300 shadow-sm' : 'bg-transparent'}`}>
            <div className="w-full px-4 md:px-8 lg:px-16">
                <div className="flex items-center justify-between h-24">
                    {/* Logo */}
                    <NavLink 
                        to='/' 
                        onClick={() => {
                            setMobileMenuOpen(false);
                            setExpandedCategories([]);
                        }}
                        className="flex-shrink-0"
                    >
                        <img src={logo_fournitest} alt='Fournitest' className="h-20 md:h-32" />
                    </NavLink>
                    
                    {/* Desktop Navigation Links */}
                    <div className="hidden lg:flex items-center space-x-12">
                        <NavLink 
                            to='/' 
                            className={({ isActive }) => 
                                `text-gray-700 hover:text-primary transition-colors font-normal ${isActive ? 'text-primary' : ''}`
                            }
                            onClick={() => {
                                setShowMegaMenu(false);
                                setExpandedCategories([]);
                            }}
                        >
                            {t('nav.home')}
                        </NavLink>
                        
                        {/* Products Dropdown */}
                        <div 
                            className="relative"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button className="flex items-center space-x-1 text-gray-700 hover:text-primary transition-colors font-normal">
                                <span>{t('nav.products')}</span>
                                <TiArrowSortedDown size={15} />
                            </button>
                            
                            {/* Mega Menu */}
                            {showMegaMenu && Array.isArray(categories) && categories.length > 0 && (
                                <div className="absolute left-0 top-full mt-2 w-screen max-w-4xl bg-white shadow-xl rounded-lg z-50 border border-gray-100">
                                    <div className="grid grid-cols-3 gap-4 p-6">
                                        {/* Left Column - Special Links */}
                                        <div className="space-y-2">
                                            <h3 className="font-semibold text-sm text-gray-500 uppercase mb-3">Accès rapide</h3>
                                            <div 
                                                className="block px-4 py-2 text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                                onClick={() => {
                                                    navigate('/nouveaute');
                                                    setShowMegaMenu(false);
                                                }}
                                            >
                                                {t('nav.new')}
                                            </div>
                                            <div 
                                                className="block px-4 py-2 text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                                onClick={() => {
                                                    const promotionTitle = t('nav.promotion');
                                                    const promotionCategory = categories.find(cat => 
                                                        cat.title === promotionTitle
                                                    );
                                                    
                                                    if (promotionCategory) {
                                                        navigate(`/products/${promotionCategory.title}`, { 
                                                            state: { categoryId: promotionCategory.id } 
                                                        });
                                                    } else {
                                                        navigate('/usedscales');
                                                    }
                                                    setShowMegaMenu(false);
                                                }}
                                            >
                                                {t('nav.promotion')}
                                            </div>
                                            <div 
                                                className="block px-4 py-2 text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                                onClick={() => {
                                                    navigate('/products');
                                                    setShowMegaMenu(false);
                                                }}
                                            >
                                                {t('nav.productslist') || 'Tous les produits'}
                                            </div>
                                        </div>
                                        
                                        {/* Middle Column - Categories */}
                                        <div className="space-y-1">
                                            <h3 className="font-semibold text-sm text-gray-500 uppercase mb-3">Catégories</h3>
                                            <div className="max-h-96 overflow-y-auto">
                                                {Array.isArray(categories) && categories.map(category => (
                                                    <div 
                                                        key={category.id} 
                                                        className="flex items-center justify-between px-4 py-2 text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                                        onMouseEnter={() => setHoveredCategoryId(category.id)}
                                                        onClick={() => handleCategoryClick(category.title, category.id)}
                                                    >
                                                        <span>{category.title}</span>
                                                        {subCategories[category.id] && subCategories[category.id].length > 0 && (
                                                            <MdKeyboardArrowRight size={16} />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        {/* Right Column - Subcategories */}
                                        <div className="space-y-1">
                                            <h3 className="font-semibold text-sm text-gray-500 uppercase mb-3">Sous-catégories</h3>
                                            <div className="max-h-96 overflow-y-auto">
                                                {hoveredCategoryId && subCategories[hoveredCategoryId] && Array.isArray(subCategories[hoveredCategoryId]) && subCategories[hoveredCategoryId].length > 0 ? (
                                                    subCategories[hoveredCategoryId].map(subCategory => (
                                                        <div
                                                            key={subCategory.id}
                                                            className="block px-4 py-2 text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                                            onClick={() => {
                                                                const category = categories.find(cat => cat.id === hoveredCategoryId);
                                                                if (category) {
                                                                    handleSubCategoryClick(
                                                                        category.title,
                                                                        subCategory.title,
                                                                        category.id,
                                                                        subCategory.id
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            {subCategory.title}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="px-4 py-2 text-gray-400 text-sm">
                                                        {t('nav.selectCategory') || 'Sélectionnez une catégorie'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <NavLink 
                            to='/aboutus' 
                            className={({ isActive }) => 
                                `text-gray-700 hover:text-primary transition-colors font-normal ${isActive ? 'text-primary' : ''}`
                            }
                            onClick={() => setShowMegaMenu(false)}
                        >
                            {t('nav.about')}
                        </NavLink>
                        
                        <NavLink 
                            to='/contact' 
                            className={({ isActive }) => 
                                `text-gray-700 hover:text-primary transition-colors font-normal ${isActive ? 'text-primary' : ''}`
                            }
                            onClick={() => setShowMegaMenu(false)}
                        >
                            {t('nav.contact')}
                        </NavLink>
                    </div>
                    
                    {/* Right Section - Icons */}
                    <div className="flex items-center space-x-4">
                        {/* Wishlist Icon */}
                        <NavLink 
                            to="/wishlist" 
                            className="hidden md:block relative text-gray-700 hover:text-primary transition-colors"
                        >
                            <AiOutlineHeart size={24} />
                            {wishlistCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                    {wishlistCount > 99 ? '99+' : wishlistCount}
                                </span>
                            )}
                        </NavLink>
                        
                        {/* Search Icon */}
                        <button className="hidden md:block text-gray-700 hover:text-primary transition-colors">
                            <FiSearch size={22} />
                        </button>
                        
                        {/* Cart or Dashboard */}
                        {user ? (
                            <NavLink 
                                to='/admin/dashboard' 
                                className="text-gray-700 hover:text-primary transition-colors"
                            >
                                <MdDashboard size={24} />
                            </NavLink>
                        ) : (
                            <NavLink 
                                to='/panier' 
                                className="relative text-gray-700 hover:text-primary transition-colors"
                            >
                                <PiShoppingCartSimple size={24} />
                                {cart.length > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                        {cart.length > 99 ? '99+' : cart.length}
                                    </span>
                                )}
                            </NavLink>
                        )}
                        
                        {/* Mobile Menu Toggle */}
                        <button 
                            className="lg:hidden text-gray-700 hover:text-primary transition-colors"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="lg:hidden bg-white border-t border-gray-200 fixed inset-x-0 top-20 bottom-0 overflow-y-auto">
                    <div className="px-4 py-4 space-y-4">
                        <NavLink 
                            to='/' 
                            className={({ isActive }) => 
                                `block py-2 text-[#202C4D] hover:text-primary transition-colors font-normal ${isActive ? 'text-primary' : ''}`
                            }
                            onClick={() => {
                                setMobileMenuOpen(false);
                                setExpandedCategories([]);
                            }}
                        >
                            {t('nav.home')}
                        </NavLink>
                        
                        {/* Products Expandable Section */}
                        <div className="border-b border-gray-200 pb-4">
                            <div className="flex items-center justify-between py-2">
                                <span className="text-[#202C4D] font-normal">{t('nav.products')}</span>
                                <button 
                                    className="p-2 cursor-pointer hover:text-primary transition-colors"
                                    onClick={() => toggleCategory('products-main')}
                                >
                                    <span className={`transform transition-transform inline-block ${expandedCategories.includes('products-main') ? 'rotate-180' : ''}`}>
                                        ▼
                                    </span>
                                </button>
                            </div>
                            
                            {expandedCategories.includes('products-main') && (
                                <div className="mt-2 space-y-4 pl-4">
                                    {/* Quick Access Links */}
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Accès rapide</h4>
                                        <div 
                                            className="block px-3 py-2 text-sm text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                            onClick={() => {
                                                navigate('/products');
                                                setMobileMenuOpen(false);
                                                setExpandedCategories([]);
                                            }}
                                        >
                                            {t('nav.productslist') || 'Tous les produits'}
                                        </div>
                                        <div 
                                            className="block px-3 py-2 text-sm text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                            onClick={() => {
                                                navigate('/nouveaute');
                                                setMobileMenuOpen(false);
                                                setExpandedCategories([]);
                                            }}
                                        >
                                            {t('nav.new')}
                                        </div>
                                        <div 
                                            className="block px-3 py-2 text-sm text-[#202C4D] hover:bg-gray-50 hover:text-primary rounded cursor-pointer transition-colors"
                                            onClick={() => {
                                                const promotionTitle = t('nav.promotion');
                                                const promotionCategory = categories.find(cat => 
                                                    cat.title === promotionTitle
                                                );
                                                
                                                if (promotionCategory) {
                                                    navigate(`/products/${promotionCategory.title}`, { 
                                                        state: { categoryId: promotionCategory.id } 
                                                    });
                                                } else {
                                                    navigate('/usedscales');
                                                }
                                                setMobileMenuOpen(false);
                                                setExpandedCategories([]);
                                            }}
                                        >
                                            {t('nav.promotion')}
                                        </div>
                                    </div>
                                    
                                    {/* Categories with Subcategories */}
                                    <div className="space-y-2">
                                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Catégories</h4>
                                        {Array.isArray(categories) && categories.map(category => (
                                            <div key={category.id} className="border-b border-gray-100 pb-2 last:border-b-0">
                                                <div className="flex items-center justify-between">
                                                    <span 
                                                        className="text-sm text-[#202C4D] hover:text-primary font-normal cursor-pointer flex-1 px-3 py-2 rounded hover:bg-gray-50 transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCategoryClick(category.title, category.id);
                                                        }}
                                                    >
                                                        {category.title}
                                                    </span>
                                                    {subCategories[category.id] && Array.isArray(subCategories[category.id]) && subCategories[category.id].length > 0 && (
                                                        <button 
                                                            className="p-2 cursor-pointer hover:text-primary transition-colors"
                                                            onClick={() => toggleCategory(category.id)}
                                                        >
                                                            <span className={`transform transition-transform inline-block text-xs ${expandedCategories.includes(category.id) ? 'rotate-180' : ''}`}>
                                                                ▼
                                                            </span>
                                                        </button>
                                                    )}
                                                </div>
                                                {expandedCategories.includes(category.id) && subCategories[category.id] && Array.isArray(subCategories[category.id]) && (
                                                    <div className="mt-2 ml-3 space-y-1">
                                                        <h5 className="text-xs font-semibold text-gray-400 uppercase mb-1 px-3">Sous-catégories</h5>
                                                        {subCategories[category.id].map(subCategory => (
                                                            <div
                                                                key={subCategory.id}
                                                                className="text-sm text-gray-600 hover:text-primary hover:bg-gray-50 cursor-pointer px-3 py-2 rounded transition-colors"
                                                                onClick={() => handleSubCategoryClick(
                                                                    category.title,
                                                                    subCategory.title,
                                                                    category.id,
                                                                    subCategory.id
                                                                )}
                                                            >
                                                                {subCategory.title}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <NavLink 
                            to='/aboutus' 
                            className={({ isActive }) => 
                                `block py-2 text-[#202C4D] hover:text-primary transition-colors font-normal ${isActive ? 'text-primary' : ''}`
                            }
                            onClick={() => {
                                setMobileMenuOpen(false);
                                setExpandedCategories([]);
                            }}
                        >
                            {t('nav.about')}
                        </NavLink>
                        
                        <NavLink 
                            to='/contact' 
                            className={({ isActive }) => 
                                `block py-2 text-[#202C4D] hover:text-primary transition-colors font-normal ${isActive ? 'text-primary' : ''}`
                            }
                            onClick={() => {
                                setMobileMenuOpen(false);
                                setExpandedCategories([]);
                            }}
                        >
                            {t('nav.contact')}
                        </NavLink>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
